import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import FormViewer from 'pages/submitted_forms_by_employee/FormViewer'
import {
  DetailModal, DataTable, Stat, Pill, ViewButton,
  assignmentStatusClass, assignmentStatusLabel, verificationClass, expiryClass,
  statusKey, isOverdue, formatDate, materialSummary, openCredentialFile
} from './complianceShared'
const th = 'py-3 px-4 font-medium text-gray-900'
const td = 'py-3 px-4'

// function DocumentsDetail({ group, onViewDocument }) {
//   return (
//     <DataTable
//       headers={[{ label: 'Sr. No.' }, { label: 'Document' }, { label: 'Category' }, { label: 'Action', center: true }]}
//       empty='No documents.'
//       rows={group.items.map((doc, index) => (
//         <tr key={doc._id || index} className='bg-white border-b hover:bg-gray-50'>
//           <th scope='row' className={th}>{index + 1}</th>
//           <td className={`${td} text-gray-900`}>{doc.title || doc.originalName || doc.fileName || '-'}</td>
//           <td className={td}>{typeof doc.category === 'string' ? doc.category : doc.category?.name || '—'}</td>
//           <td className={`${td} text-center`}><ViewButton title='View document' onClick={() => onViewDocument(doc)} /></td>
//         </tr>
//       ))}
//     />
//   )
// }

function SectionTable({ title, rows }) {
  if (!rows.length) return null
  return (
    <div className="px-2 ">
      <h3 className="text-base font-semibold mb-2">{title}</h3>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full table-fixed text-xs text-left text-slate-600">
          <colgroup>
            <col className="w-24" />
            <col />
            <col className="w-40" />
            <col className="w-28" />
          </colgroup>
          <thead className="bg-slate-50 text-xs text-slate-700 border-b border-slate-200">
            <tr>
              <th className="py-3 px-4 text-center">Sr. No.</th>
              <th className="py-3 px-4 text-left">Document</th>
              <th className="py-3 px-4 text-left">Category</th>
              <th className="py-3 px-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.key} className="bg-white border-b border-slate-200 hover:bg-gray-50">
                <td className="py-3 px-4 text-center text-gray-900">{index + 1}</td>
                <td className="py-3 px-4 text-left text-gray-900">{row.name}</td>
                <td className="py-3 px-4 text-left text-gray-900">{row.category}</td>
                <td className="py-3 px-4 text-center">
                  <ViewButton title="View document" onClick={row.onView} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DocumentsDetail({ group, onViewDocument }) {
  const [credentials, setCredentials] = useState([])
  const [submission, setSubmission] = useState(null)
  const [backgroundChecks, setBackgroundChecks] = useState([])
  const [selectedForm, setSelectedForm] = useState(null)
  const [downloadingReportId, setDownloadingReportId] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    apiGet(apiPath.submitFormByEmployee, { page: 1, pageSize: 100 })
      .then((result) => {
        const docs = result?.data?.results?.finalData?.docs || []
        const match = docs.find((item) =>
          (group.code && item?.Employee?.employeeId === group.code) ||
          (group.name && item?.Employee?.name === group.name)
        ) || null
        if (cancelled) return
        setSubmission(match)
        const employeeId = match?.Employee?._id
        if (!employeeId) return
        return Promise.all([
          apiGet(apiPath.trainingCertificationList, { page: 1, pageSize: 100, userId: employeeId })
            .then((res) => {
              if (!cancelled) setCredentials(res?.data?.results?.docs || [])
            })
            .catch(() => { if (!cancelled) setCredentials([]) }),
          apiGet(apiPath.backgroundCheck, {
            employeeID: employeeId,
            employeeEmail: match?.Employee?.email || '',
            page: 1,
            limit: 20
          })
            .then((res) => {
              if (!cancelled) setBackgroundChecks(res?.data?.success ? res.data.results?.checks || [] : [])
            })
            .catch(() => { if (!cancelled) setBackgroundChecks([]) })
        ])
      })
      .catch(() => { if (!cancelled) setSubmission(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [group.code, group.name])

  const trainingRows = credentials.filter((item) => item?.type === 'training')
  const certificationRows = credentials.filter((item) => item?.type === 'certification')
  const employeeForms = (Array.isArray(submission?.EmployeeFilledForms) ? submission.EmployeeFilledForms.flat(3) : [])
    .filter((form) => form && typeof form === 'object' && (form._id || form.formId || form.formType))
  const otherDocuments = submission?.EmployeeDocuments?.length ? submission.EmployeeDocuments : group.items
  const employeeId = submission?.Employee?._id
  const employeeEmail = String(submission?.Employee?.email || '').toLowerCase()
  const employeeReports = backgroundChecks.filter((check) => {
    const checkEmployeeId = String(check.employeeID?._id || check.employeeID || '')
    const checkEmail = String(check.employeeData?.email || '').toLowerCase()
    const samePerson = (employeeId && checkEmployeeId === String(employeeId)) || (employeeEmail && checkEmail === employeeEmail)
    return samePerson && (check.status === 'Complete' || check.reportAvailable)
  })

  const downloadReport = async (check) => {
    if (!check?._id || downloadingReportId) return
    setDownloadingReportId(check._id)
    try {
      const token = localStorage.getItem('token')
      const baseURL = process.env.REACT_APP_API_BASE_URL || ''
      const response = await axios.get(`${baseURL}${apiPath.backgroundCheck}/${check._id}/report-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const blobUrl = window.URL.createObjectURL(blob)
      const opened = window.open(blobUrl, '_blank', 'noopener,noreferrer')
      if (!opened) {
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `background_check_report_${check.credibledUUID || check._id}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
      }
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000)
    } catch (error) {
      window.alert('Report is not available yet.')
    } finally {
      setDownloadingReportId('')
    }
  }

  const labelText = (value, fallback) => {
    const raw = String(value || '').trim()
    if (!raw) return fallback
    return raw.replace(/[-_]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
  }

  const formName = (form) => labelText(
    form?.formType || form?.formData?.formType || form?.formData?.formName,
    'Employee Agreement'
  )
  const allowedForms = new Set(['background release', 'employee agreement'])
  const visibleForms = employeeForms.filter((form) => {
    if (!allowedForms.has(formName(form).toLowerCase())) return false
    if (!form?.jobID) return true
    return String(form.jobID) === String(submission?._id)
  })
  const formsByTitle = visibleForms.reduce((groups, form, index) => {
    const title = formName(form)
    if (!groups[title]) groups[title] = []
    groups[title].push({
      key: form._id || form.formId || index,
      name: title,
      category: 'Form',
      onView: () => form?.formData && setSelectedForm({ structure: form.formData, data: form })
    })
    return groups
  }, {})

  if (loading) {
    return <p className="py-6 text-center text-sm text-gray-500">Loading...</p>
  }

  return (
    <>
      <SectionTable
        title="Training"
        rows={trainingRows.map((item) => ({
          key: item._id,
          name: item.course || 'Training',
          category: 'Training',
          onView: () => openCredentialFile(item)
        }))}
      />
      <SectionTable
        title="Certification"
        rows={certificationRows.map((item) => ({
          key: item._id,
          name: item.certificate || 'Certification',
          category: 'Certification',
          onView: () => openCredentialFile(item)
        }))}
      />
      {Object.entries(formsByTitle).map(([title, rows]) => (
        <SectionTable key={title} title={title} rows={rows} />
      ))}
      <SectionTable
        title="Uploaded Documents"
        rows={otherDocuments.map((doc, index) => ({
          key: doc._id || index,
          name: labelText(doc?.title || doc?.originalName || doc?.fileName, 'Document'),
          category: 'Document',
          onView: () => onViewDocument(doc)
        }))}
      />
      <SectionTable
        title="Background verification"
        rows={employeeReports.map((check) => ({
          key: check._id,
          name: 'Background Check Report',
          category: 'Background',
          onView: () => downloadReport(check)
        }))}
      />

      {selectedForm && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-[960px] max-h-[90vh]">
            <FormViewer formStructure={selectedForm.structure} formData={selectedForm.data} onClose={() => setSelectedForm(null)} />
          </div>
        </div>
      )}
    </>
  )
}

function TrainingDetail({ group }) {
  return (
    <>
      <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
        <Stat label='Assigned' value={group.counts.assigned} />
        <Stat label='In progress' value={group.counts.in_progress} tone='blue' />
        <Stat label='Completed' value={group.counts.completed} tone='green' />
        <Stat label='Overdue' value={group.overdue} tone={group.overdue ? 'red' : 'slate'} />
      </div>
      <DataTable
        headers={[{ label: 'Sr. No.' }, { label: 'Title' }, { label: 'Due date' }, { label: 'Status' }, { label: 'Progress' }]}
        empty='No assessments.'
        rows={group.items.map((item, index) => {
          const summary = materialSummary(item)
          return (
            <tr key={item._id} className='bg-white border-b hover:bg-gray-50'>
              <th scope='row' className={th}>{index + 1}</th>
              <td className={`${td} text-gray-900`}>{item.title || 'Assessment'}</td>
              <td className={`${td} whitespace-nowrap`}>
                {formatDate(item.dueDate)}
                {isOverdue(item) && <Pill className='ml-2 bg-red-50 text-red-500 border-red-100'>Overdue</Pill>}
              </td>
              <td className={td}>
                <Pill className={assignmentStatusClass(statusKey(item))}>{assignmentStatusLabel(statusKey(item))}</Pill>
              </td>
              <td className={td}>
                {summary.total > 0 ? `${summary.done}/${summary.total} completed` : '—'}
                {summary.percent > 0 && summary.percent < 100 ? ` · ${summary.percent}% watched` : ''}
              </td>
            </tr>
          )
        })}
      />
    </>
  )
}

function CertificationDetail({ group }) {
  return (
    <>
      <div className='grid grid-cols-3 gap-3'>
        <Stat label='Pending' value={group.counts.pending} tone='amber' />
        <Stat label='Verified' value={group.counts.verified} tone='green' />
        <Stat label='Rejected' value={group.counts.rejected} tone={group.counts.rejected ? 'red' : 'slate'} />
      </div>
      <DataTable
        headers={[
          { label: 'Sr. No.' }, { label: 'Certificate' }, { label: 'Issuer' }, { label: 'Issue date' },
          { label: 'Expiry date' }, { label: 'Verification' }, { label: 'Expiry' }, { label: 'File', center: true }
        ]}
        empty='No certificates.'
        rows={group.items.map((item, index) => {
          const verification = item.verification || 'pending'
          const expiry = item.expiryTracking || 'No expiry'
          return (
            <tr key={item._id} className='bg-white border-b hover:bg-gray-50'>
              <th scope='row' className={th}>{index + 1}</th>
              <td className={`${td} text-gray-900`}>{item.certificate || '—'}</td>
              <td className={td}>{item.issuer || '—'}</td>
              <td className={`${td} whitespace-nowrap`}>{formatDate(item.issueDate)}</td>
              <td className={`${td} whitespace-nowrap`}>{formatDate(item.expiryDate)}</td>
              <td className={td}><Pill className={`capitalize ${verificationClass(verification)}`}>{verification}</Pill></td>
              <td className={td}><Pill className={expiryClass(expiry)}>{expiry}</Pill></td>
              <td className={`${td} text-center`}><ViewButton title='Open file' onClick={() => openCredentialFile(item)} /></td>
            </tr>
          )
        })}
      />
    </>
  )
}

const SUBTITLE = {
  documents: (g) => `${g.items.length} uploaded ${g.items.length === 1 ? 'document' : 'documents'}`,
  training: (g) => `${g.items.length} ${g.items.length === 1 ? 'assessment' : 'assessments'} · ${g.completion}% completed`,
  certification: (g) => `${g.items.length} ${g.items.length === 1 ? 'certificate' : 'certificates'}`
}

function RecordDetail({ type, group, onClose, onViewDocument ,credentialRecords }) {
  return (
    <DetailModal
      title={`${group.name}${group.code ? ` (${group.code})` : ''}`}
      subtitle={SUBTITLE[type](group)}
      onClose={onClose}
    >
      {type === 'documents' && <DocumentsDetail group={group} onViewDocument={onViewDocument} credentialRecords={credentialRecords} /> }
      {type === 'training' && <TrainingDetail group={group} />}
      {type === 'certification' && <CertificationDetail group={group} />}
    </DetailModal>
  )
}

export default RecordDetail